import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { cn } from '../../lib/format.js';
import { useUi } from '../../stores/ui.js';
import { useSession } from '../../stores/session.js';
import { MegaMenu } from './MegaMenu.jsx';
import { Wordmark } from './Wordmark.jsx';

/**
 * The masthead. Constant across both worlds — the chrome, typography, and layout
 * never change, only the accent token underneath them does.
 *
 * It sticks on scroll but only gains its border and background once the page has
 * moved, so the hero meets the top of the window rather than a bar.
 */

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openWorld, setOpenWorld] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  const openCart = useUi((state) => state.openCart);
  const toggleMenu = useUi((state) => state.toggleMenu);
  const openSearch = useUi((state) => state.openSearch);
  const sessionStatus = useSession((state) => state.status);

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories.list({ tree: true, activeOnly: true }),
    queryFn: () => api.get('/categories', { params: { tree: 'true', activeOnly: 'true' } }),
    staleTime: 10 * 60 * 1000,
  });

  const { data: cart } = useQuery({
    queryKey: queryKeys.cart.detail(),
    queryFn: () => api.get('/cart'),
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A route change must close the mega menu, or it hangs over the new page.
  useEffect(() => setOpenWorld(null), [location.pathname]);

  const worlds = [
    { key: 'fragrance', label: 'Fragrance' },
    { key: 'sport', label: 'Sport' },
  ];

  /**
   * Which world the current URL is in, or null. `NavLink` would mark both worlds
   * current on any `/shop` route, because it matches on pathname and ignores the
   * query — leaving three links accented at once.
   */
  const currentWorld =
    location.pathname === '/shop' ? new URLSearchParams(location.search).get('world') : null;

  /**
   * `NavLink` matches on pathname alone, so "New in" and "Everything" — both of
   * which resolve to `/shop` — would highlight together. Each link states its
   * own rule instead.
   */
  const links = [
    {
      to: '/shop?isNewArrival=true',
      label: 'New in',
      isActive: ({ pathname, search }) =>
        pathname === '/shop' && new URLSearchParams(search).get('isNewArrival') === 'true',
    },
    {
      to: '/shop',
      label: 'Everything',
      isActive: ({ pathname, search }) => pathname === '/shop' && search === '',
    },
    { to: '/brands', label: 'Brands', isActive: ({ pathname }) => pathname.startsWith('/brands') },
    { to: '/journal', label: 'Journal', isActive: ({ pathname }) => pathname.startsWith('/journal') },
  ];

  /** A small delay on close stops the menu vanishing while crossing the gap. */
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenWorld(null), 140);
  };

  const cancelClose = () => clearTimeout(closeTimer.current);

  const itemCount = cart?.itemCount ?? 0;

  return (
    <header
      className={cn(
        'sticky top-0 z-40 transition-[background-color,border-color] duration-300',
        scrolled ? 'border-b border-mist bg-paper/92 backdrop-blur-md' : 'border-b border-transparent bg-paper',
      )}
      onMouseLeave={scheduleClose}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded focus:bg-ink focus:px-4 focus:py-2 focus:text-xs focus:text-white"
      >
        Skip to content
      </a>

      <div className="mx-auto flex h-16 max-w-(--container-content) items-center gap-3 px-4 sm:px-6 lg:h-[72px]">
        <button
          type="button"
          onClick={toggleMenu}
          aria-label="Open menu"
          className="-ml-2 grid size-10 place-items-center rounded text-ink transition-colors hover:bg-mist/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:hidden"
        >
          <svg viewBox="0 0 20 20" className="size-5" fill="currentColor" aria-hidden="true">
            <path d="M2.5 5h15v1.5h-15zm0 4.25h15v1.5h-15zm0 4.25h15V15h-15z" />
          </svg>
        </button>

        <Link
          to="/"
          className="shrink-0 rounded focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
          aria-label="Golden Eagle, home"
        >
          <Wordmark className="h-5 lg:h-[22px]" />
        </Link>

        <nav aria-label="Main" className="ml-6 hidden items-center gap-1 lg:flex">
          {worlds.map((world) => {
            const active = currentWorld === world.key;

            return (
              <div key={world.key} onMouseEnter={() => { cancelClose(); setOpenWorld(world.key); }}>
                <Link
                  to={`/shop?world=${world.key}`}
                  data-world={world.key}
                  aria-expanded={openWorld === world.key}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'relative flex h-[72px] items-center px-3 text-xs transition-colors',
                    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                    openWorld === world.key || active ? 'text-accent' : 'text-ink hover:text-accent',
                  )}
                >
                  {world.label}
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute inset-x-3 bottom-0 h-px origin-left bg-accent transition-transform duration-300',
                      openWorld === world.key || active ? 'scale-x-100' : 'scale-x-0',
                    )}
                  />
                </Link>
              </div>
            );
          })}

          {/* The flat links. Every one goes somewhere real — `isNewArrival` and
              `sort` are filters the shop already reads from the URL, so "New in"
              is the catalogue rather than a second page to maintain. */}
          {links.map((link) => {
            const active = link.isActive(location);

            return (
              <Link
                key={link.label}
                to={link.to}
                aria-current={active ? 'page' : undefined}
                onMouseEnter={() => setOpenWorld(null)}
                className={cn(
                  'px-3 text-xs transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                  active ? 'text-ink' : 'text-muted hover:text-ink',
                )}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            onClick={openSearch}
            aria-label="Search"
            className="grid size-10 place-items-center rounded text-ink transition-colors hover:bg-mist/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <svg viewBox="0 0 20 20" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <circle cx="9" cy="9" r="5.5" />
              <path d="m13.2 13.2 3.3 3.3" strokeLinecap="round" />
            </svg>
          </button>

          <Link
            to={sessionStatus === 'authenticated' ? '/account' : '/sign-in'}
            aria-label={sessionStatus === 'authenticated' ? 'Your account' : 'Sign in'}
            className="hidden size-10 place-items-center rounded text-ink transition-colors hover:bg-mist/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:grid"
          >
            <svg viewBox="0 0 20 20" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <circle cx="10" cy="6.75" r="3.25" />
              <path d="M3.75 16.5c.9-3 3.3-4.5 6.25-4.5s5.35 1.5 6.25 4.5" strokeLinecap="round" />
            </svg>
          </Link>

          <button
            type="button"
            onClick={openCart}
            data-cart-target
            aria-label={itemCount > 0 ? `Bag, ${itemCount} items` : 'Bag, empty'}
            className="relative grid size-10 place-items-center rounded text-ink transition-colors hover:bg-mist/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <svg viewBox="0 0 20 20" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M4.25 6.5h11.5l-1 9.25a1.25 1.25 0 0 1-1.24 1.12H6.49a1.25 1.25 0 0 1-1.24-1.12z" />
              <path d="M7.25 8.25V6a2.75 2.75 0 0 1 5.5 0v2.25" strokeLinecap="round" />
            </svg>
            {itemCount > 0 && (
              <span
                key={itemCount}
                className="absolute -top-0.5 -right-0.5 grid min-w-[18px] place-items-center rounded-full bg-accent px-1 font-mono text-[10px] leading-[18px] text-white motion-safe:animate-[badge-pop_320ms_cubic-bezier(0.34,1.56,0.64,1)]"
                data-numeric
              >
                {itemCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <MegaMenu
        world={openWorld}
        categories={categories}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onNavigate={() => setOpenWorld(null)}
      />
    </header>
  );
};

export default Header;
